import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Skeleton,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { getTransHeaderExpenseAmendmentStatus } from "src/apis/controllers/expenses";
import { CssTextField } from "src/qubCommon/Inputs";
import {
  getResponseMessage,
  getResponsePayload,
  isErrorResponse,
} from "./headerPayload";
import "./ChangeStatusDialog.css";

const REQUIRED_ACTION_MESSAGE = "Δεν έχετε επιλέξει ενέργεια!";
const LOAD_ERROR_MESSAGE = "Δεν φορτώθηκαν οι διαθέσιμες ενέργειες.";
const SAVE_ERROR_MESSAGE = "Προέκυψε σφάλμα κατά την αλλαγή κατάστασης.";

const getOptionId = (option) => option?.id ?? option?.Id;
const getOptionTitle = (option) => option?.title ?? option?.Title ?? "";
const getOptionDescription = (option) =>
  option?.description ?? option?.Description ?? "";

const hasSelectedOption = (value) =>
  value !== null && value !== undefined && value !== "";

const LoadingSkeleton = () => (
  <Box sx={{ width: "100%", padding: 2 }}>
    <Skeleton sx={{ width: "50%", height: "44px" }} />
    <Skeleton sx={{ width: "40%", height: "35px" }} />
    <Skeleton sx={{ width: "100%", height: "30px" }} />
    <Skeleton sx={{ width: "100%", height: "30px" }} />
    <Skeleton sx={{ width: "100%", height: "30px" }} />
    <Skeleton sx={{ width: "100%", height: "30px" }} />
    <Skeleton sx={{ width: "100%", height: "120px" }} />
  </Box>
);

const ChangeStatusDialog = ({
  open,
  transHeaderId,
  title,
  currentStatus,
  isSaving = false,
  onCancel,
  onConfirm,
}) => {
  const amendmentForm = useForm();
  const [loading, setLoading] = useState(true);
  const [amendmentOptions, setAmendmentOptions] = useState([]);
  const [checkedOption, setCheckedOption] = useState(null);
  const [error, setError] = useState(null);
  const [comments, setComments] = useState("");

  useEffect(() => {
    let isActive = true;

    const resetState = () => {
      setAmendmentOptions([]);
      setCheckedOption(null);
      setComments("");
      setError(null);
      setLoading(true);
    };

    const fetchOptions = async () => {
      if (!transHeaderId) {
        setAmendmentOptions([]);
        setError("Δεν βρέθηκε αναγνωριστικό παραστατικού.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const res = await getTransHeaderExpenseAmendmentStatus(transHeaderId);

        if (!isActive) return;

        if (isErrorResponse(res)) {
          setAmendmentOptions([]);
          setError(getResponseMessage(res, LOAD_ERROR_MESSAGE));
          return;
        }

        const payload = getResponsePayload(res);
        setAmendmentOptions(Array.isArray(payload) ? payload : []);
      } catch (fetchError) {
        if (!isActive) return;

        setAmendmentOptions([]);
        setError(getResponseMessage(fetchError, LOAD_ERROR_MESSAGE));
      } finally {
        if (isActive) setLoading(false);
      }
    };

    if (!open) {
      resetState();
      return undefined;
    }

    setCheckedOption(null);
    setComments("");
    fetchOptions();

    return () => {
      isActive = false;
    };
  }, [open, transHeaderId]);

  const handleClose = () => {
    if (isSaving) return;

    onCancel?.();
  };

  const handleCheckboxChange = (optionId) => {
    setError(null);
    setCheckedOption(optionId);
  };

  const handleSubmit = async () => {
    if (!hasSelectedOption(checkedOption)) {
      setError(REQUIRED_ACTION_MESSAGE);
      return;
    }

    try {
      setError(null);
      const result = await onConfirm?.({
        newStatusId: checkedOption,
        comments,
      });

      if (result?.ok === false) {
        setError(result.message ?? SAVE_ERROR_MESSAGE);
      }
    } catch (submitError) {
      setError(getResponseMessage(submitError, SAVE_ERROR_MESSAGE));
    }
  };

  return (
    <FormProvider {...amendmentForm}>
      <Dialog
        onClose={handleClose}
        aria-labelledby="expense-change-status-dialog-title"
        open={open}
        fullWidth
        maxWidth="md"
      >
        {loading ? (
          <LoadingSkeleton />
        ) : (
          <>
            <DialogTitle
              sx={{ m: 0, p: 2, fontSize: "16px" }}
              id="expense-change-status-dialog-title"
            >
              Μεταβολή Κατάστασης - {title}
            </DialogTitle>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              disabled={isSaving}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
            <Divider />
            <DialogContent
              sx={{
                width: "100%",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            >
              <div className="expense-change-status-form-container">
                <Typography variant="subtitle1" sx={{ marginBottom: 1 }}>
                  Τρέχουσα κατάσταση παραστατικού: <b>{currentStatus}</b>
                </Typography>
                {error ? (
                  <b className="expense-change-status-error">{error}</b>
                ) : null}
                {amendmentOptions.length === 0 ? (
                  <div className="expense-change-status-empty">
                    <b>Δεν βρέθηκαν διαθέσιμες ενέργειες!</b>
                  </div>
                ) : (
                  <>
                    <table className="expense-change-status-actions-table">
                      <thead>
                        <tr>
                          <th>Διαθέσιμη Ενέργεια</th>
                          <th>Νέα κατάσταση παραστατικού</th>
                        </tr>
                      </thead>
                      <tbody>
                        {amendmentOptions.map((option, index) => {
                          const optionId = getOptionId(option);

                          return (
                            <tr key={optionId ?? index}>
                              <td>
                                <div className="expense-change-status-checkbox-container">
                                  <input
                                    type="checkbox"
                                    id={`expense-change-status-option-${optionId}`}
                                    checked={checkedOption === optionId}
                                    disabled={isSaving}
                                    onChange={() =>
                                      handleCheckboxChange(optionId)
                                    }
                                  />
                                  {getOptionDescription(option)}
                                </div>
                              </td>
                              <td>{getOptionTitle(option)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    <div className="expense-change-status-form-group">
                      <label htmlFor="expense-change-status-comments">
                        Σχόλια
                      </label>
                      <CssTextField
                        id="expense-change-status-comments"
                        multiline
                        minRows={5}
                        fullWidth
                        fixedPadding={8}
                        value={comments}
                        disabled={isSaving}
                        onChange={(event) => setComments(event.target.value)}
                      />
                    </div>

                    <div className="expense-change-status-actions">
                      <Button
                        variant="contained"
                        color="secondary"
                        sx={{ color: "white" }}
                        onClick={handleClose}
                        disabled={isSaving}
                      >
                        Επιστροφή
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isSaving}
                      >
                        Αποθήκευση
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </DialogContent>
          </>
        )}
      </Dialog>
    </FormProvider>
  );
};

export default ChangeStatusDialog;
